import type { ReleaseEntry } from './types';

/**
 * Normalize a tag for comparison: trimmed + lowercased. Backend tags are exact, case-sensitive
 * strings, but the client-side filter is deliberately forgiving — a host can pass `['Billing']`
 * and still match a `billing` tag, and stray whitespace is ignored.
 */
function normalizeTag(tag: string): string {
  return tag.trim().toLowerCase();
}

/**
 * Restrict `entries` to those carrying at least one of `tags` (OR semantics). Matching is
 * case-insensitive and whitespace-trimmed; an empty or blank filter returns the entries unchanged.
 *
 * This runs in the browser. The public-client API returns each note's `tags` but has **no**
 * server-side tag filter, so the SDK selects locally — which also means it works identically for
 * the live backend and the bundled dummy content. See DESIGN.md.
 */
export function filterByTags(entries: ReleaseEntry[], tags: readonly string[] | undefined): ReleaseEntry[] {
  const wanted = new Set((tags ?? []).map(normalizeTag).filter(Boolean));
  if (wanted.size === 0) return entries;
  return entries.filter(entry => (entry.tags ?? []).some(tag => wanted.has(normalizeTag(tag))));
}
